import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useLang } from '../../i18n';
import { Img } from '../Img';
import { ProductTile } from '../ProductTile';
import { altOf } from '../../lib/seasonMedia';

/** La foto di un'idea, col filetto del colore della stagione. */
const FotoIdea = ({ foto, lang, className = '', ariaHidden }) => (
  <figure
    aria-hidden={ariaHidden}
    className={`relative aspect-square overflow-hidden bg-carta-2 md:aspect-[4/5] ${className}`}
  >
    <Img
      src={foto.src}
      alt={altOf(foto, lang)}
      width={1200}
      aspectRatio={4 / 5}
      sizes="(min-width: 768px) 45vw, 100vw"
      className="h-full w-full object-cover"
    />
    <span aria-hidden className="absolute inset-x-0 bottom-0 h-[3px]" style={{ background: 'var(--accento)' }} />
  </figure>
);

/**
 * «La tavola si apparecchia»: le tre idee della stagione.
 *
 * Da tablet in su la foto dell'idea resta ferma a sinistra e cambia in
 * dissolvenza mentre a destra scorrono le idee; l'idea attiva e' quella che
 * attraversa la fascia centrale dello schermo. L'ordine del DOM resta lineare
 * (foto, poi testo, poi pezzi), quindi tastiera e screen reader non vedono lo
 * sticky. Su telefono e con reduced motion: ogni foto sopra il suo testo.
 */
export const TavolaSiApparecchia = ({ title, ideas, foto }) => {
  const { t, lang } = useLang();
  const ridotto = useReducedMotion();
  const [attiva, setAttiva] = useState(0);
  const lista = useRef(null);

  useEffect(() => {
    if (ridotto || !lista.current) return undefined;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) setAttiva(Number(e.target.dataset.indice));
        }),
      { rootMargin: '-45% 0px -45% 0px' },
    );
    lista.current.querySelectorAll('li[data-indice]').forEach((li) => io.observe(li));
    return () => io.disconnect();
  }, [ridotto, ideas.length]);

  return (
    <section data-testid="season-ideas" className="py-14 md:py-20">
      <div className="wrap">
        <h2 className="mt-3 max-w-[26ch] text-[clamp(2rem,5.6vw,3.8rem)] leading-[1.04]">{title}</h2>
      </div>

      <div className="wrap mt-10 md:grid md:grid-cols-[0.95fr_1.05fr] md:gap-16">
        {!ridotto && (
          <div className="hidden md:block">
            <div data-testid="season-ideas-sticky" className="sticky top-24 aspect-[4/5]">
              {foto.map((f, i) => (
                <FotoIdea
                  key={f.src}
                  foto={f}
                  lang={lang}
                  ariaHidden={i !== attiva}
                  className={`!absolute inset-0 transition-opacity duration-700 ${
                    i === attiva ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        <ol ref={lista} className={ridotto ? 'md:col-span-2' : ''}>
          {ideas.map((idea, i) => (
            <li
              key={idea.title}
              data-indice={i}
              className="flex flex-col justify-center py-11 md:min-h-[70svh] md:py-16"
            >
              <FotoIdea
                foto={foto[i]}
                lang={lang}
                className={ridotto ? 'mb-8 max-w-xl' : 'mb-8 md:hidden'}
              />
              <h3 className="mt-3 max-w-[20ch] text-[clamp(1.7rem,5.4vw,2.8rem)] leading-[1.06]">{idea.title}</h3>
              <p className="prosa mt-4 max-w-[44ch]">{idea.text}</p>

              <ul className="fila fila-margini -mx-[var(--gutter)] mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto px-[var(--gutter)] pb-1 md:mx-0 md:grid md:grid-cols-3 md:gap-5 md:overflow-visible md:px-0">
                {(idea.products || []).map((p) => (
                  <li key={p.sku} className="w-[46%] shrink-0 snap-start sm:w-[32%] md:w-auto [&_h3]:line-clamp-2">
                    <ProductTile product={p} />
                  </li>
                ))}
              </ul>
              {!idea.products?.length && <p className="text-sm text-inchiostro/65">{t('common.empty')}</p>}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
